import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  deleteTheme,
  getAvailableThemes,
  getDefaultUserTheme,
  getTheme,
  setDefaultUserTheme,
} from '../../api/resource';
import { ThemeInfo } from '../models/state.model';
import { AvailableTheme, CurrentTheme } from '../../models/profile';
import { RootState } from '../store';

/**
 * Определяет системную тему пользователя
 */
export const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const initialState: ThemeInfo = {
  mode: 'create',
  themeId: null,
  availableThemes: [],
  currentTheme: null,
  loading: false,
  deleteLoading: false,
};

type ThemeItem = Omit<AvailableTheme, 'scope'>;

const transformThemesData = (data: any): AvailableTheme[] => {
  return Object.entries(data).flatMap(
    ([parentKey, items]) =>
      (items as ThemeItem[])?.map((item) => ({
        ...item,
        scope: parentKey,
      })) ?? [],
  );
};

export const fetchAvailableThemes = createAsyncThunk('theme/fetchAvailableThemes', async () => {
  const res = await getAvailableThemes();
  return transformThemesData(res.data);
});

export const fetchCurrentTheme = createAsyncThunk('theme/fetchCurrentTheme', async () => {
  const theme = getSystemTheme();
  const res = await getDefaultUserTheme(theme);
  return res.data;
});

export const fetchDeleteTheme = createAsyncThunk(
  'theme/deleteTheme',
  async (themeId: string, { rejectWithValue }) => {
    try {
      await deleteTheme(themeId);
      return themeId;
    } catch (error) {
      return rejectWithValue('Ошибка при удалении темы');
    }
  },
);

export const selectAndApplyTheme = createAsyncThunk(
  'theme/selectAndApplyTheme',
  async (themeId: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const targetTheme = state.themeEditor.availableThemes.find((t) => t.id === themeId);

    if (!targetTheme) {
      return rejectWithValue('Тема не найдена');
    }
    const res = await getTheme(themeId);
    const themeInfo: CurrentTheme = {
      uuid: targetTheme.id,
      name: targetTheme.name,
      config: res.data,
    };

    await setDefaultUserTheme(themeId);

    return themeInfo;
  },
);

const themeEditorSlice = createSlice({
  name: 'themeEditor',
  initialState,
  reducers: {
    setCreateMode(state) {
      state.mode = 'create';
      state.themeId = null;
    },
    setEditMode(state, action: PayloadAction<string>) {
      state.mode = 'edit';
      state.themeId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAvailableThemes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAvailableThemes.fulfilled, (state, action) => {
      state.availableThemes = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAvailableThemes.rejected, (state) => {
      state.availableThemes = [];
      state.loading = false;
    });
    builder.addCase(fetchCurrentTheme.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(fetchCurrentTheme.fulfilled, (state, action) => {
      state.currentTheme = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchCurrentTheme.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(fetchDeleteTheme.pending, (state) => {
      state.deleteLoading = true;
    });
    builder.addCase(fetchDeleteTheme.fulfilled, (state, action) => {
      state.availableThemes = state.availableThemes.filter((theme) => theme.id !== action.payload);
      state.deleteLoading = false;
    });
    builder.addCase(fetchDeleteTheme.rejected, (state) => {
      state.deleteLoading = false;
    });
    builder.addCase(selectAndApplyTheme.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(selectAndApplyTheme.fulfilled, (state, action) => {
      state.currentTheme = action.payload;
      state.loading = false;
    });
    builder.addCase(selectAndApplyTheme.rejected, (state, action) => {
      state.loading = false;
    });
  },
});

export const { setCreateMode, setEditMode } = themeEditorSlice.actions;

const themeEditorReducer = themeEditorSlice.reducer;

export default themeEditorReducer;
